// The Traffic / Salik / Parking invoice page - one component, the kind's config in invoiceKinds.js (DUP-21).
import React, { useState, useEffect } from 'react';
import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import { saveAs } from 'file-saver';
import JSZip from 'jszip';
import Button from '../../../ui/Button';
import IconButton from '../../../ui/IconButton';
import Badge from '../../../ui/Badge';
import Card from '../../../ui/Card';
import Icon from '../../../ui/Icon';
import { ICONS } from '../../../ui/icon-map';
import Tabs from '../../../ui/Tabs/Tabs';
import Dialog from '../../../ui/Dialog/Dialog';
import { Field, Input, Select, RadioCards } from '../../../ui/Field/Field';
import { toast } from '../../../ui/feedback';
import LoadingSpinner from '../../../components/LoadingSpinner';
import RequirePermission from '../../../components/RequirePermission';
import InvoiceNumberField, { useInvoiceNumber } from '../../../components/InvoiceNumberField';
import { INVOICE_PREFIX, invoiceCounterOn } from '../../../services/invoiceNumbers';
import { usePermissions } from '../../../hooks/usePermissions';
import { PERMISSIONS } from '../../../constants/permissions';
import logger from '../../../utils/logger';
import { useAuth } from '../../../contexts/AuthContext';
import { adminService } from '../../../services/adminService';
import { contractsService } from '../../../services/firestore';
import { invygoIdsFor } from '../../../services/invygoIds';
import { companyService } from '../../../services/companyService';
import {
    filterRowsWithValidAmount, singleTable, drawSingleInvoice, drawConsolidatedInvoice,
    invoiceDateOf, refOf, rowsWithoutNumber, invoiceBookingOf,
} from './invoiceKinds';
import { dubaiToday } from './invoiceDates';

const readSheet = async (file) => {
    const workbook = file.name.toLowerCase().endsWith('.csv')
        ? XLSX.read(await file.text(), { type: 'string' })
        : XLSX.read(await file.arrayBuffer(), { type: 'array' });
    return XLSX.utils.sheet_to_json(workbook.Sheets[workbook.SheetNames[0]]);
};

const downloadTemplate = (template) => {
    if (template.type === 'csv') {
        saveAs(new Blob([template.headers.join(',')], { type: 'text/csv' }), template.name);
        return;
    }
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, XLSX.utils.aoa_to_sheet([template.headers]), 'Template');
    const out = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    saveAs(new Blob([out], { type: 'application/octet-stream' }), template.name);
};

const csvCell = (v) => {
    const s = v === null || v === undefined ? '' : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

const pdfDoc = () => new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

/** The invoice as it will be printed (the same table the PDF draws, every cell centred). */
function InvoicePreview({ kind, data }) {
    const { row, invoiceDate, trnNumber, companyName, companyLocation } = data;
    const table = singleTable(kind, row);
    const last = table.columns.length - 1;
    const cell = 'border border-gray-400 p-3 text-center';
    return (
        <div className="bg-white p-8 font-sans text-black" style={{ fontFamily: 'Calibri, Arial, sans-serif', lineHeight: '1.4' }}>
            <div className="text-center mb-6">
                <h1 className="text-4xl font-bold text-black mb-4">Tax Invoice</h1>
            </div>
            <div className="flex justify-between mb-4">
                <span className="font-medium">Date: {invoiceDate}</span>
                <span className="font-medium">Ref: {refOf(kind, row)}</span>
            </div>
            <div className="mb-6"><span className="font-medium">TRN#: {trnNumber}</span></div>
            <div className="mb-6">
                <div className="font-medium">{companyName}</div>
                <div>{companyLocation}</div>
                <div>Dubai, U.A.E.</div>
            </div>
            <div className="mb-6"><div className="font-medium">SUB: Micro Lease Cars</div></div>
            <div className="mb-6">
                {kind.greeting.map((line) => <div key={line}>{line}</div>)}
            </div>
            <table className="w-full border-collapse border border-gray-400 mb-6">
                <thead>
                    <tr className="bg-gray-100">
                        {table.columns.map((c) => <th key={c.title} className={`${cell} font-bold`}>{c.title}</th>)}
                    </tr>
                </thead>
                <tbody>
                    {table.rows.map((cells, r) => (
                        <tr key={r}>
                            {cells.map((c, i) => (
                                <td key={i} className={cell}>
                                    {Array.isArray(c) ? c.map((line, k) => <div key={k}>{line}</div>) : c}
                                </td>
                            ))}
                        </tr>
                    ))}
                    {table.totals.map((t, k) => {
                        const grand = k === table.totals.length - 1;
                        return (
                            <tr key={t.label}>
                                <td colSpan={t.labelFrom} />
                                <td colSpan={last - t.labelFrom} className={`${cell} ${grand ? 'font-bold bg-gray-300' : 'bg-gray-100'}`}>{t.label}</td>
                                <td className={`${cell} ${grand ? 'font-bold bg-gray-300' : 'bg-gray-100'}`}>{t.value}</td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
            <div className="mb-4"><div className="font-bold underline">General Conditions:</div></div>
            <div className="mb-6"><div>Terms of Payment: within 7 days</div></div>
            <div className="mb-8"><div>Thanking you and assuring you of our best co-operation and services at all times.</div></div>
            <div>
                <div>Best Regards,</div>
                <div className="font-bold mt-4">Saudian Alwefaq Rent A Car</div>
            </div>
        </div>
    );
}

export default function KindInvoiceGenerator({ kind }) {
    const { currentUser } = useAuth();
    const { hasPermission } = usePermissions();
    const [entryMode, setEntryMode] = useState('upload'); // 'upload' or 'manual'
    const [excelFile, setExcelFile] = useState(null);
    const [uploadedRows, setUploadedRows] = useState([]);
    const [status, setStatus] = useState('');
    const [selectedDate, setSelectedDate] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    // Registered companies (the customer on the invoice)
    const [registeredCompanies, setRegisteredCompanies] = useState([]);
    const [selectedCompanyId, setSelectedCompanyId] = useState('');
    const [companyName, setCompanyName] = useState('');
    const [companyLocation, setCompanyLocation] = useState('');
    const [trnNumber, setTrnNumber] = useState('');

    const [showPreview, setShowPreview] = useState(false);
    const [previewData, setPreviewData] = useState(null);

    // The invoice-number dialog (manual entry, or sheet rows without a number)
    const [showRefModal, setShowRefModal] = useState(false);
    const [pending, setPending] = useState(null);              // {rows, consolidated?} waiting for numbers
    const [generationMode, setGenerationMode] = useState('consolidated'); // 'split' or 'consolidated'
    // the numbering while the shared counter is off: ALWFQ-<typed>; a split ZIP numbers on from it on every kind
    // (digits: 100, 101, 102...; other text: _1, _2...) - Parking used to repeat one number on every invoice
    const number = useInvoiceNumber(showRefModal, 0, { prefix: INVOICE_PREFIX, mode: 'sequence' });

    const [searchTerm, setSearchTerm] = useState('');
    const [isSearching, setIsSearching] = useState(false);
    const newRow = (extra = {}) => ({ id: Date.now() + Math.random(), ...kind.emptyRow(), ...extra });
    const [manualRows, setManualRows] = useState(() => [{ ...kind.emptyRow(), id: 1 }]);

    useEffect(() => {
        companyService.getAllCompanies().then(setRegisteredCompanies).catch((e) => logger.error(e));
    }, []);

    const handleCompanyChange = (e) => {
        const id = e.target.value;
        setSelectedCompanyId(id);
        const comp = registeredCompanies.find(c => c.id === id);
        setCompanyName(comp ? comp.name : '');
        setCompanyLocation(comp ? comp.address : '');
        setTrnNumber(comp ? comp.trn : '');
    };

    const handleManualChange = (id, field, value) => {
        setManualRows(prev => prev.map(row => (row.id === id ? { ...row, [field]: value } : row)));
    };
    const addManualRow = () => setManualRows(prev => [...prev, newRow()]);
    const removeManualRow = (id) => {
        if (manualRows.length > 1) setManualRows(prev => prev.filter(row => row.id !== id));
    };

    const handleSmartSearch = async () => {
        if (!searchTerm) return;
        setIsSearching(true);
        setStatus('Searching contracts...');
        try {
            const results = await contractsService.searchContracts(searchTerm);
            if (results && results.length > 0) {
                // an Invygo contract's Booking ID is Invygo's own id (invoiceBookingOf); a failed read leaves it
                // empty to type, and says so - never Ejar's own booking number in its place
                let invygoIds = null;
                try {
                    invygoIds = await invygoIdsFor(results.map((res) => res.contract_no));
                } catch (err) {
                    logger.warn('[invoices] Invygo booking ids not loaded', err);
                }
                const found = results.map((res) => newRow(kind.fromContract({ ...res, booking_number: invoiceBookingOf(res, invygoIds) })));
                // replace the single empty row, else append
                setManualRows(prev => {
                    const isFirstEmpty = prev.length === 1 && !kind.customerOf(prev[0]);
                    return isFirstEmpty ? found : [...prev, ...found];
                });
                setStatus(invygoIds ? `Found ${results.length} records.`
                    : `Found ${results.length} records. Invygo booking IDs could not be loaded - type the Booking ID of an Invygo contract.`);
            } else {
                setStatus('No records found.');
            }
        } catch (error) {
            logger.error(error);
            setStatus('Error searching.');
        } finally {
            setIsSearching(false);
        }
    };

    const handleExcelUpload = async (e) => {
        const file = e.target.files && e.target.files[0];
        if (!file) return;
        setExcelFile(file);
        setStatus('');
        try {
            setUploadedRows(await readSheet(file));
        } catch (error) {
            logger.error(error);
            setUploadedRows([]);
            setStatus('The file could not be read. Please check the file format.');
        }
    };

    const manualSource = () => manualRows.map((r) => (kind.normalizeManual ? kind.normalizeManual(r) : r));

    const generateCSVMirror = () => {
        const data = entryMode === 'upload' ? uploadedRows : manualSource();
        if (entryMode === 'upload' && data.length === 0) {
            setStatus('Please upload a CSV file first.');
            return;
        }
        const headers = kind.template.headers;
        const csv = [headers.join(','), ...data.map((row) => headers.map((h) => csvCell(row[h])).join(','))].join('\n');
        saveAs(new Blob([csv], { type: 'text/csv' }), 'Invygo Upload.csv');
        adminService.logUserAction('EXPORT_CSV', `Exported ${kind.filePrefix} CSV`, currentUser, { rowCount: data.length });
        setStatus('Invygo Upload generated successfully!');
    };

    const invoiceDay = () => {
        const day = selectedDate || dubaiToday(); // the Dubai day, not the UTC one
        if (!selectedDate) setSelectedDate(day);
        return day;
    };

    const handlePreview = () => {
        if (entryMode === 'upload' && !excelFile) {
            setStatus('Please upload a file first.');
            return;
        }
        const day = invoiceDay();
        if (!trnNumber) {
            setStatus('Please enter the TRN number first.');
            return;
        }
        let rows = entryMode === 'upload' ? uploadedRows
            : manualSource().filter(r => r[kind.amountKey] || kind.customerOf(r));
        if (entryMode === 'manual' && rows.length === 0 && manualRows.length > 0) rows = [manualSource()[0]];
        if (!rows.length) {
            setStatus('No data found.');
            return;
        }
        setPreviewData({ row: rows[0], invoiceDate: invoiceDateOf(rows[0], day), trnNumber, companyName, companyLocation });
        setShowPreview(true);
        setStatus('');
    };

    /** The rows to invoice (amounts checked), or null when there is nothing valid (the reason is shown). */
    const rowsToInvoice = () => {
        let rows;
        if (entryMode === 'upload') {
            if (!excelFile) {
                setStatus('Please upload a file first.');
                return null;
            }
            rows = uploadedRows;
        } else {
            rows = manualSource().filter(r => r[kind.amountKey]);
            if (rows.length === 0) {
                toast.error('Please enter the amount for at least one row.');
                return null;
            }
        }
        const { validRows, skipped } = filterRowsWithValidAmount(kind, rows);
        if (skipped.length > 0) {
            toast.warning(`${skipped.length} row(s) have an unreadable amount and were skipped: ${skipped.join(', ')}`, 10000);
        }
        if (validRows.length === 0) {
            setStatus('No valid rows to generate — all amounts were unreadable.');
            return null;
        }
        return validRows;
    };

    const handleConvert = () => {
        if (!trnNumber) {
            setStatus('Please enter the TRN number first.');
            return;
        }
        const rows = rowsToInvoice();
        if (!rows) return;
        // Manual rows take their numbers from the invoice-number service (today: the typed reference, as before);
        // sheet rows keep their own number - only with the shared counter on are the rows without one numbered.
        if (entryMode === 'manual' || (invoiceCounterOn() && rowsWithoutNumber(kind, rows).length > 0)) {
            setPending({ rows });
            setShowRefModal(true);
        } else {
            generate(rows, []);
        }
    };

    const consolidatedWanted = (rows) => entryMode === 'manual' && rows.length > 1 && generationMode === 'consolidated';
    const numbersNeeded = pending
        ? (consolidatedWanted(pending.rows) ? 1 : (entryMode === 'manual' ? pending.rows.length : rowsWithoutNumber(kind, pending.rows).length))
        : 1;

    const confirmNumbers = async () => {
        if (!pending) return;
        let numbers;
        try {
            numbers = await number.claim(numbersNeeded, currentUser?.email || '');
        } catch {
            return; // the dialog shows why
        }
        setShowRefModal(false);
        generate(pending.rows, numbers);
        setPending(null);
    };

    const generate = async (rows, numbers) => {
        const day = invoiceDay();
        const pageDate = invoiceDateOf(null, day);
        setIsLoading(true);
        setStatus('Converting...');
        try {
            const response = await fetch('/Invoice.jpg');
            const imgData = new Uint8Array(await response.arrayBuffer());
            const company = { imgData, trn: trnNumber, companyName, companyLocation };

            if (consolidatedWanted(rows)) {
                const ref = numbers[0];
                const doc = pdfDoc();
                drawConsolidatedInvoice(doc, kind, rows, { ...company, date: pageDate, ref });
                doc.save(`${kind.filePrefix}_Consolidated_${ref}.pdf`);
                adminService.logUserAction('GENERATE_INVOICE', `Generated ${kind.filePrefix} Consolidated Invoice`, currentUser, { count: rows.length, reference: ref });
            } else {
                // the issued numbers go to the rows without one, in order
                let next = 0;
                const numbered = rows.map((row) => {
                    const own = entryMode === 'upload' ? String(row[kind.invoiceNoKey] ?? '').trim() : '';
                    return own ? row : { ...row, RefNumber: numbers[next++] || '' };
                });
                if (numbered.length === 1 && entryMode === 'manual') {
                    const row = numbered[0];
                    const doc = pdfDoc();
                    drawSingleInvoice(doc, kind, row, { ...company, date: invoiceDateOf(row, day) });
                    doc.save(`${kind.filePrefix}_${refOf(kind, row)}.pdf`);
                    adminService.logUserAction('GENERATE_INVOICE', `Generated ${kind.filePrefix} Invoice (PDF)`, currentUser, { count: 1, mode: 'manual', reference: refOf(kind, row) });
                } else {
                    const zip = new JSZip();
                    const folder = zip.folder(`${kind.filePrefix}_Invoices`);
                    numbered.forEach((row, i) => {
                        const doc = pdfDoc();
                        drawSingleInvoice(doc, kind, row, { ...company, date: invoiceDateOf(row, day) });
                        const name = String(refOf(kind, row) || kind.fileIdOf(row) || `Invoice_${i + 1}`).replace(/[^a-z0-9_-]/gi, '_');
                        folder.file(`${kind.filePrefix}_${name}.pdf`, doc.output('blob'));
                    });
                    saveAs(await zip.generateAsync({ type: 'blob' }), `${kind.filePrefix}_Invoices_${day}.zip`);
                    adminService.logUserAction('GENERATE_INVOICE', `Generated ${kind.filePrefix} Invoices (ZIP)`, currentUser,
                        { count: rows.length, mode: entryMode === 'upload' ? 'bulk_zip' : 'manual_bulk', references: numbers });
                }
            }
            setStatus('Success! Invoices Generated.');
        } catch (error) {
            logger.error(error);
            setStatus('Error generating invoices.');
        } finally {
            setIsLoading(false);
        }
    };

    const modeTabs = [
        { id: 'upload', label: 'Upload Excel', icon: <Icon glyph={ICONS.upload} size="sm" />, permission: PERMISSIONS.invoice_mode_upload },
        { id: 'manual', label: 'Manual Entry', icon: <Icon glyph={ICONS.edit} size="sm" />, permission: PERMISSIONS.invoice_mode_manual },
    ].filter(t => hasPermission(t.permission));
    const readOnlyCls = selectedCompanyId ? 'bg-gray-100 cursor-not-allowed' : '';
    const idp = kind.id;

    return (
        <div className="max-w-4xl mx-auto space-y-6">
            {/* Blocking progress while a generation runs */}
            <Dialog open={isLoading} onClose={() => {}} title="Please wait" size="sm" hideClose closeOnOverlay={false} closeOnEscape={false}>
                <LoadingSpinner size="lg" message={status || 'Working…'} />
            </Dialog>

            <div>
                <h2 className="text-base font-semibold text-gray-900 m-0">{kind.title}</h2>
                <p className="text-sm text-gray-500 m-0">{kind.subtitle}</p>
            </div>

            {modeTabs.length > 0 && (
                <Tabs items={modeTabs} value={entryMode} onChange={setEntryMode} variant="segmented" label="Entry mode" idPrefix={`${idp}-mode`} />
            )}

            {entryMode === 'upload' ? (
                <div role="tabpanel" id={`${idp}-mode-panel-upload`} aria-labelledby={`${idp}-mode-upload`}>
                    <RequirePermission permission={PERMISSIONS.invoice_mode_upload} fallback={null}>
                        <div className="flex flex-col gap-3">
                            <div className="flex flex-wrap justify-between items-center gap-2">
                                <label htmlFor={`${idp}-source-file`} className="text-sm font-medium text-gray-900">Source File</label>
                                <Button onClick={() => downloadTemplate(kind.template)} variant="link" size="sm" leadingIcon={<Icon glyph={ICONS.download} size="sm" />}>
                                    Download Template
                                </Button>
                            </div>
                            <div className={`relative rounded-lg border-2 border-dashed p-8 text-center transition-colors focus-within:border-purple-500 focus-within:ring-2 focus-within:ring-purple-200 ${excelFile
                                ? 'border-green-300 bg-green-50'
                                : 'border-gray-300 bg-white hover:border-purple-400 hover:bg-purple-50'}`}>
                                <input
                                    id={`${idp}-source-file`}
                                    type="file"
                                    accept={kind.accept}
                                    onChange={handleExcelUpload}
                                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                                />
                                <div className="flex flex-col items-center gap-2 pointer-events-none">
                                    <span className={`flex h-10 w-10 items-center justify-center rounded-full ${excelFile ? 'bg-green-100 text-green-700' : 'bg-purple-50 text-purple-700'}`}>
                                        <Icon glyph={excelFile ? ICONS.document : ICONS.upload} size="md" />
                                    </span>
                                    <p className="text-sm font-medium text-gray-900 m-0">{excelFile ? excelFile.name : kind.uploadLabel}</p>
                                    <p className="text-xs text-gray-500 m-0">{excelFile ? 'Click to change file' : 'Drag and drop or browse'}</p>
                                </div>
                            </div>
                        </div>
                    </RequirePermission>
                </div>
            ) : (
                <div className="space-y-4" role="tabpanel" id={`${idp}-mode-panel-manual`} aria-labelledby={`${idp}-mode-manual`}>
                    <div className="flex flex-wrap items-end gap-2">
                        <Field label="Smart Search" className="flex-1 min-w-[200px]">
                            <Input
                                placeholder="Enter Booking ID or Contract No..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && handleSmartSearch()}
                            />
                        </Field>
                        <Button onClick={handleSmartSearch} isLoading={isSearching} leadingIcon={<Icon glyph={ICONS.search} size="sm" />}>
                            Search
                        </Button>
                    </div>

                    <div className="flex flex-wrap justify-between items-center gap-2 border-b border-gray-200 pb-2">
                        <h3 className="text-sm font-semibold text-gray-900 m-0">
                            Entry Details <span className="font-normal text-gray-500">({manualRows.length} rows)</span>
                        </h3>
                        <Button onClick={addManualRow} size="sm" variant="secondary" leadingIcon={<Icon glyph={ICONS.plus} size="sm" />}>
                            Add Row
                        </Button>
                    </div>

                    <div className="space-y-4">
                        {manualRows.map((row, index) => (
                            <Card key={row.id} role="group" aria-label={`Row ${index + 1}`}>
                                <div className="mb-3 flex items-center justify-between gap-2">
                                    <Badge variant="accent" size="sm">Row {index + 1}</Badge>
                                    {manualRows.length > 1 && (
                                        <IconButton size="sm" icon={ICONS.close} label="Remove Row" tone="danger" onClick={() => removeManualRow(row.id)} />
                                    )}
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    {kind.fields.map((f) => (
                                        <Field key={f.key} label={f.label} help={f.help} className={f.wide ? 'md:col-span-2' : undefined}>
                                            <Input
                                                type={f.type || 'text'}
                                                value={row[f.key] ?? ''}
                                                onChange={(e) => handleManualChange(row.id, f.key, e.target.value)}
                                                placeholder={f.placeholder}
                                            />
                                        </Field>
                                    ))}
                                </div>
                            </Card>
                        ))}
                    </div>
                </div>
            )}

            <Card>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <Field label="Select Registered Company" className="md:col-span-2 lg:col-span-1">
                        <Select
                            value={selectedCompanyId}
                            onChange={handleCompanyChange}
                            placeholder="-- Manual Entry --"
                            options={registeredCompanies.map(c => ({ value: c.id, label: c.name }))}
                        />
                    </Field>
                    <Field label="Invoice Date">
                        <Input type="date" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} />
                    </Field>
                    <Field label="TRN Number">
                        <Input value={trnNumber} onChange={(e) => setTrnNumber(e.target.value)} className={`font-mono ${readOnlyCls}`} readOnly={!!selectedCompanyId} />
                    </Field>
                    <Field label="Company Name">
                        <Input value={companyName} onChange={(e) => setCompanyName(e.target.value)} className={readOnlyCls} readOnly={!!selectedCompanyId} />
                    </Field>
                    <Field label="Location / Address" className="md:col-span-2 lg:col-span-4">
                        <Input value={companyLocation} onChange={(e) => setCompanyLocation(e.target.value)} className={readOnlyCls} readOnly={!!selectedCompanyId} />
                    </Field>
                </div>
            </Card>

            <div className="flex flex-wrap justify-end gap-2">
                <RequirePermission permission={PERMISSIONS.preview_invoice} fallback={null}>
                    <Button onClick={handlePreview} variant="secondary" size="lg" leadingIcon={<Icon glyph={ICONS.eye} size="sm" />}>
                        Preview
                    </Button>
                </RequirePermission>
                {kind.csvMirror && (
                    <RequirePermission permission={PERMISSIONS.generate_invoice_documents} fallback={null}>
                        <Button onClick={generateCSVMirror} variant="secondary" size="lg" leadingIcon={<Icon glyph={ICONS.export} size="sm" />}>
                            Export CSV
                        </Button>
                    </RequirePermission>
                )}
                <RequirePermission permission={PERMISSIONS.generate_invoice_documents} fallback={null}>
                    <Button onClick={handleConvert} variant="primary" size="lg" leadingIcon={<Icon glyph={ICONS.download} size="sm" />}>
                        Generate Documents
                    </Button>
                </RequirePermission>
            </div>

            {status && !isLoading && !status.includes('Success') && (
                <div className="flex items-center justify-center gap-2 p-3 text-sm font-medium bg-red-50 text-red-700 rounded-lg border border-red-200" role="alert">
                    <Icon glyph={ICONS.warning} size="sm" />
                    <span>{status}</span>
                </div>
            )}
            {status && !isLoading && status.includes('Success') && (
                <div className="flex items-center justify-center gap-2 p-3 text-sm font-medium bg-green-50 text-green-700 rounded-lg border border-green-200" role="status">
                    <Icon glyph={ICONS.success} size="sm" />
                    <span>{status}</span>
                </div>
            )}

            <Dialog open={showPreview && !!previewData} onClose={() => setShowPreview(false)} title="Invoice Preview" size="xl">
                {previewData && (
                    <div className="overflow-x-auto rounded-lg border border-gray-200">
                        <InvoicePreview kind={kind} data={previewData} />
                    </div>
                )}
            </Dialog>

            <Dialog
                open={showRefModal}
                onClose={() => { setShowRefModal(false); setPending(null); }}
                title={invoiceCounterOn() ? 'Invoice number' : 'Enter Reference Number'}
                description={!invoiceCounterOn()
                    ? "Enter the starting reference number. Prefix 'ALWFQ-' is automatic."
                    : entryMode === 'upload'
                        ? `${numbersNeeded} row(s) of the sheet have no invoice number: they are numbered from this one on.`
                        : 'The next free number of the ALWFQ- series is filled in.'}
                size="sm"
                footer={(
                    <>
                        <Button onClick={() => { setShowRefModal(false); setPending(null); }} variant="secondary">Cancel</Button>
                        <Button onClick={confirmNumbers} variant="primary">Generate</Button>
                    </>
                )}
            >
                <div className="space-y-4">
                    <InvoiceNumberField state={number} count={numbersNeeded} />
                    {entryMode === 'manual' && pending && pending.rows.length > 1 && (
                        <div className="space-y-2">
                            <p className="text-sm font-medium text-gray-900 m-0">Generation Mode</p>
                            <RadioCards
                                label="Generation Mode"
                                value={generationMode}
                                onChange={(v) => setGenerationMode(v)}
                                options={[
                                    { value: 'split', label: 'Separate Invoices (ZIP)', description: 'Generates individual PDFs per row (Ref: 100, 101, 102...)' },
                                    { value: 'consolidated', label: 'Consolidated Invoice (Single PDF)', description: 'Combines all rows into one invoice table (Ref: 100)' },
                                ]}
                            />
                        </div>
                    )}
                </div>
            </Dialog>
        </div>
    );
}
